import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import DoorlyProLayout, { RequirePartnerAuth } from '../../components/DoorlyProLayout';
import { useDoorlyProAuth } from '../../context/DoorlyProAuthContext';
import RichMessageBody, { hasRichBody } from '../../components/RichMessageBody';
import MessageAttachment from '../../components/MessageAttachment';
import RichComposer from '../../components/RichComposer';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export default function DoorlyProMessages() {
  const { token } = useDoorlyProAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const bottomRef = useRef(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchMessages = () => {
    axios
      .get(`${API_URL}/api/doorlypro/messages`, { headers: authHeaders })
      .then((res) => setMessages(res.data?.messages || res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!token) return;
    fetchMessages();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // 🔑 THE SHARED COMPOSER (2026-11-02): { body_html, body } — body_html is sanitized on the server against the one
  // allowlist. The thread is re-read after a send so the bubble shows exactly what was stored.
  const handleSend = async ({ body_html, body }) => {
    if (!String(body || '').trim()) return false;
    setSending(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/doorlypro/messages`, { body: String(body).trim(), body_html }, { headers: authHeaders });
      await fetchMessages();
      return true;
    } catch (err) {
      setError('Failed to send message. Please try again.');
      return false;
    } finally {
      setSending(false);
    }
  };

  // Poll for new messages every 10 seconds
  useEffect(() => {
    if (!token) return;
    const interval = setInterval(fetchMessages, 10000);
    return () => clearInterval(interval);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const isPartnerMsg = (msg) => msg.sender_type === 'partner' || msg.sender === 'partner';

  return (
    <RequirePartnerAuth>
      <DoorlyProLayout>
        <div style={{ maxWidth: 700, height: 'calc(100vh - 140px)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ marginBottom: 16 }}>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: 0 }}>Messages</h1>
            <p style={{ fontSize: 14, color: '#6b7280', marginTop: 4 }}>Communicate directly with the Doorly team.</p>
          </div>

          {/* Message thread */}
          <div style={{
            flex: 1,
            backgroundColor: '#fff',
            borderRadius: 12,
            border: '1px solid #e5e7eb',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
            <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
              {loading ? (
                <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, marginTop: 40 }}>Loading messages...</div>
              ) : messages.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, marginTop: 40 }}>
                  No messages yet. Start a conversation below.
                </div>
              ) : (
                messages.map((msg, idx) => {
                  const mine = isPartnerMsg(msg);
                  return (
                    <div
                      key={msg.id || idx}
                      style={{
                        display: 'flex',
                        justifyContent: mine ? 'flex-end' : 'flex-start',
                        marginBottom: 12,
                      }}
                    >
                      <div
                        style={{
                          maxWidth: '70%',
                          padding: '10px 14px',
                          borderRadius: mine ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                          backgroundColor: mine ? '#3a6ad9' : '#f3f4f6',
                          color: mine ? '#fff' : '#111827',
                          fontSize: 13,
                          lineHeight: 1.5,
                        }}
                      >
                        {/* 🔑 A formatted staff message (the composer, 2026-10-31): the server's sanitised HTML; its attachment
                            opens through a 5-minute link minted for this partner's own message. Plain messages unchanged. */}
                        {hasRichBody(msg)
                          ? <RichMessageBody msg={msg} dark={mine} />
                          : <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content || msg.body || msg.message}</div>}
                        {!mine && msg.has_attachment && msg.attachment_name && (
                          <div style={{ marginTop: 8 }}>
                            <MessageAttachment msgId={msg.id} name={msg.attachment_name} token={token}
                              mintPath={`/api/doorlypro/messages/${msg.id}/attachment-url`} />
                          </div>
                        )}
                        <div style={{ fontSize: 10, marginTop: 4, opacity: 0.7, textAlign: mine ? 'right' : 'left' }}>
                          {msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={bottomRef} />
            </div>

            {/* Input area */}
            <div style={{ borderTop: '1px solid #e5e7eb', padding: 16 }}>
              {error && (
                <div style={{ fontSize: 12, color: '#ff082f', marginBottom: 8 }}>{error}</div>
              )}
              {/* 🔑 THE ONE MESSAGE BOX (the shared composer, approved v3 2026-11-02). No Attach file here, as before. */}
              <RichComposer
                testId="doorlypro-composer"
                onSend={handleSend}
                placeholder={`Type a message… (${/Mac|iPhone|iPad/.test(navigator.platform || '') ? '⌘' : 'Ctrl+'}↵ to send)`}
                sendLabel="Send"
                disabled={sending}
                minLines={2}
                maxLines={6}
                fontSize={16}
                formatBarKey="doorlypro_composer_format_bar"
              />
            </div>
          </div>
        </div>
      </DoorlyProLayout>
    </RequirePartnerAuth>
  );
}
